import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { api } from "../../services/api.js";
import { config as appConfig } from "../../services/config.js";
import { useMediaQuery } from "react-responsive";
import { Spin, Input, message, Pagination, Drawer, Badge, Button } from "antd";
import { SearchOutlined, ShoppingCartOutlined, EyeOutlined, SlidersOutlined } from "@ant-design/icons";
import { useCarrito } from "../../context/CarritoContext.js";
import { useClient } from "../../context/ClientContext.js";
import { useAuth } from "../../context/AuthContext.js";
import { resolveRad } from "../../templates/4/utils.js";
import img from '../../assets/sin_resultados.png';
import { FiltrosCatalogo } from "./Filtros.js";

const BACKEND = appConfig.URL_API.replace("/api", "");
const resolveImg = (url) => {
  if (!url) return null;
  return url.startsWith("http") ? url : BACKEND + url;
};
const resolveAdjunto = (adj) => {
  const src = adj?.adjunto;
  if (!src) return null;
  if (src.startsWith('http') || src.startsWith('/')) return resolveImg(src);
  return resolveImg(`/uploads/productos/${adj.id_tabla}/${src}`);
};
const resolveProductImg = (producto) => {
  const adj = producto.adjuntos?.[0];
  return adj ? resolveAdjunto(adj) : resolveImg(producto.imagen);
};

const parseCategoriaIds = (value) => (value || "")
  .split(",")
  .map(id => Number(id.trim()))
  .filter(id => Number.isFinite(id) && id > 0);

const sameIds = (a, b) => a.length === b.length && a.every((id, index) => id === b[index]);

// Sin sesión llega "disponible" y no la cantidad; con sesión llegan los dos.
const sinStock = (p) => (p.disponible !== undefined ? !p.disponible : p.stock <= 0);

function ProductCard({ producto, theme, rad, onAdd, loadingId, clientId, clientPath, navigate, quickBuy, showStock, showOutOfStock, showPrices, isMobile, user }) {
  const [hovered, setHovered] = useState(false);
  const [addHov, setAddHov] = useState(false);
  const isOutOfStock = showOutOfStock && sinStock(producto);

  const minVariantPrice = producto.precio_min ?? null;
  const maxVariantPrice = producto.precio_max ?? null;
  const displayPrice = minVariantPrice != null ? minVariantPrice : (producto.precio > 0 ? producto.precio : null);
  const hasPrice = displayPrice != null;
  const showDesde = minVariantPrice != null && maxVariantPrice !== minVariantPrice;
  const promo = producto.promo;
  const promoLabel = promo?.codigo === 'descuento_porcentaje' ? `-${promo.valor}%`
    : promo?.codigo === 'descuento_fijo' ? `-$${Number(promo.valor).toFixed(0)}` : null;
  const primary = theme.primaryColor || "#3b5bdb";
  const primaryText = theme.primaryTextColor || "#fff";
  const textColor = theme.textColor || "#1a1a2e";

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        border: `1.5px solid ${hovered && !isOutOfStock ? primary : "rgba(0,0,0,0.08)"}`,
        cursor: "pointer",
        transition: "border-color 0.22s ease, box-shadow 0.22s ease",
        boxShadow: hovered && !isOutOfStock ? `0 6px 28px ${primary}1a` : "0 1px 4px rgba(0,0,0,0.06)",
        opacity: isOutOfStock ? 0.82 : 1,
        display: "flex", flexDirection: "column", height: "100%",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setAddHov(false); }}
      onClick={() => navigate(clientPath(`/catalogo/${producto.id}`))}>

      {/* Image */}
      <div style={{
        height: 200, background: "#f5f5f5",
        borderRadius: "10px 10px 0 0",
        display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
        position: "relative", flexShrink: 0,
      }}>
        {resolveProductImg(producto)
          ? <img src={resolveProductImg(producto)} alt={producto.nombre}
              style={{ width: "78%", height: "78%", objectFit: "contain" }}
              onError={e => { e.target.style.display = "none"; }}/>
          : <div style={{ fontSize: 52, opacity: 0.1 }}>📦</div>
        }
        {isOutOfStock && (
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            background: "rgba(0,0,0,0.52)", color: "#fff",
            fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
            textAlign: "center", padding: "5px 0", textTransform: "uppercase",
          }}>
            Sin stock
          </div>
        )}
        {promoLabel && !isOutOfStock && (
          <div style={{
            position: "absolute", top: 8, left: 8,
            background: "#e53e3e", color: "#fff",
            fontSize: 11, fontWeight: 700, padding: "3px 8px",
            borderRadius: 6, letterSpacing: "0.03em",
          }}>
            {promoLabel}
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: "14px 16px 16px", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ fontSize: 11, color: "#bbb", marginBottom: 6, fontWeight: 500, letterSpacing: "0.02em", minHeight: 16 }}>
          {producto.codigo ? `CÓD. ${producto.codigo}` : ""}
        </div>
        <div style={{
          fontSize: 14, fontWeight: 600, color: textColor, lineHeight: 1.45,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          overflow: "hidden", marginBottom: 10,
          minHeight: "2.9em",
        }}>
          {producto.nombre}
        </div>
        {/* Price — always reserves height (taller on mobile to fit stacked promo price) */}
        <div style={{ minHeight: isMobile ? 42 : 26, marginBottom: showStock ? 4 : 0 }}>
          {showPrices && hasPrice && !isOutOfStock && (
            promo?.precio_final != null ? (
              isMobile ? (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span style={{ fontSize: 12, color: "#999", textDecoration: "line-through" }}>
                    ${displayPrice.toFixed(2)}
                  </span>
                  <span style={{ fontSize: 17, fontWeight: 700, color: "#e53e3e" }}>
                    ${promo.precio_final.toFixed(2)}
                  </span>
                </div>
              ) : (
                <div>
                  <span style={{ fontSize: 13, color: "#999", textDecoration: "line-through", marginRight: 6 }}>
                    ${displayPrice.toFixed(2)}
                  </span>
                  <span style={{ fontSize: 17, fontWeight: 700, color: "#e53e3e" }}>
                    ${promo.precio_final.toFixed(2)}
                  </span>
                </div>
              )
            ) : (
              <div style={{ fontSize: 17, fontWeight: 700, color: primary }}>
                {showDesde && <span style={{ fontSize: 12, fontWeight: 400 }}>Desde </span>}
                ${displayPrice.toFixed(2)}
              </div>
            )
          )}
        </div>
        {/* Stock — always reserves height when showStock is on */}
        {showStock && (
          <div style={{ fontSize: 12, color: "#999", marginBottom: 6, minHeight: 18 }}>
            {user && !isOutOfStock && `${producto.stock} disponibles`}
          </div>
        )}

        {/* Buttons */}
        <div style={{
          overflow: quickBuy ? "visible" : "hidden",
          maxHeight: quickBuy ? "none" : (hovered ? 72 : 0),
          opacity: quickBuy ? 1 : (hovered ? 1 : 0),
          transition: quickBuy ? "none" : "max-height 0.28s cubic-bezier(0.4,0,0.2,1), opacity 0.2s ease",
          marginTop: quickBuy ? 12 : 0,
        }}>
          <div style={{ paddingTop: quickBuy ? 0 : 12, display: "flex", gap: 8, alignItems: "stretch" }}>
            <button
              style={{
                flex: 1, padding: "10px 0", borderRadius: rad,
                border: `1.5px solid ${primary}`,
                background: addHov ? "transparent" : primary,
                color: addHov ? primary : primaryText,
                fontWeight: 600, fontSize: 12,
                cursor: (hasPrice && !isOutOfStock) ? "pointer" : "not-allowed",
                transition: "background 0.18s, color 0.18s",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                opacity: (hasPrice && !isOutOfStock) ? 1 : 0.35,
              }}
              onMouseEnter={() => setAddHov(true)}
              onMouseLeave={() => setAddHov(false)}
              onClick={e => { e.stopPropagation(); if (hasPrice && !isOutOfStock) onAdd(producto); }}
              disabled={user && (!hasPrice || isOutOfStock || loadingId === producto.id)}>
              <ShoppingCartOutlined style={{ fontSize: 13 }}/>
              {loadingId === producto.id ? "Agregando..." : isOutOfStock ? "Sin stock" : producto.tiene_variantes ? "Ver opciones" : "Agregar al carrito"}
            </button>
            {!isMobile && (
              <button
                style={{
                  width: 40, flexShrink: 0, borderRadius: rad,
                  border: `1.5px solid ${primary}`,
                  background: primary, color: primaryText,
                  cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
                onClick={e => { e.stopPropagation(); navigate(clientPath(`/catalogo/${producto.id}`)); }}>
                <EyeOutlined style={{ fontSize: 15 }}/>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductListItem({ producto, theme, rad, onAdd, loadingId, clientId, clientPath, navigate, quickBuy, showStock, showOutOfStock, showPrices, isMobile, user }) {
  const [hovered, setHovered] = useState(false);
  const [addHov, setAddHov] = useState(false);
  const [eyeHov, setEyeHov] = useState(false);
  const isOutOfStock = showOutOfStock && sinStock(producto);

  const minVariantPrice = producto.precio_min ?? null;
  const maxVariantPrice = producto.precio_max ?? null;
  const displayPrice = minVariantPrice != null ? minVariantPrice : (producto.precio > 0 ? producto.precio : null);
  const hasPrice = displayPrice != null;
  const showDesde = minVariantPrice != null && maxVariantPrice !== minVariantPrice;
  const promo = producto.promo;
  const promoLabel = promo?.codigo === 'descuento_porcentaje' ? `-${promo.valor}%`
    : promo?.codigo === 'descuento_fijo' ? `-$${Number(promo.valor).toFixed(0)}` : null;
  const primary = theme.primaryColor || "#3b5bdb";
  const primaryText = theme.primaryTextColor || "#fff";
  const textColor = theme.textColor || "#1a1a2e";

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: rad || 12,
        border: `1.5px solid ${hovered ? primary : "rgba(0,0,0,0.08)"}`,
        cursor: "pointer",
        transition: "border-color 0.22s ease, box-shadow 0.22s ease",
        boxShadow: hovered ? `0 4px 20px ${primary}15` : "0 1px 4px rgba(0,0,0,0.06)",
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "12px 16px",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setAddHov(false); }}
      onClick={() => navigate(clientPath(`/catalogo/${producto.id}`))}>

      {/* Image */}
      <div style={{
        width: 90, height: 90, flexShrink: 0,
        background: "#f5f5f5", borderRadius: rad || 8,
        display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
        position: "relative",
      }}>
        {resolveProductImg(producto)
          ? <img src={resolveProductImg(producto)} alt={producto.nombre}
              style={{ width: "80%", height: "80%", objectFit: "contain" }}
              onError={e => { e.target.style.display = "none"; }}/>
          : <div style={{ fontSize: 32, opacity: 0.15 }}>📦</div>
        }
        {isOutOfStock && (
          <div style={{
            position: "absolute", inset: 0,
            background: "rgba(0,0,0,0.42)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span style={{ color: "#fff", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Agotado
            </span>
          </div>
        )}
        {promoLabel && !isOutOfStock && (
          <div style={{
            position: "absolute", top: 4, left: 4,
            background: "#e53e3e", color: "#fff",
            fontSize: 9, fontWeight: 700, padding: "2px 6px",
            borderRadius: 4,
          }}>
            {promoLabel}
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {producto.codigo && (
          <div style={{ fontSize: 11, color: "#bbb", marginBottom: 4, fontWeight: 500, letterSpacing: "0.02em" }}>
            CÓD. {producto.codigo}
          </div>
        )}
        <div style={{
          fontSize: 15, fontWeight: 600, color: textColor, lineHeight: 1.45,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}>
          {producto.nombre}
        </div>
        {showStock && user && !isOutOfStock && (
          <div style={{ fontSize: 12, color: "#999", marginTop: 5 }}>
            {producto.stock} disponibles
          </div>
        )}
      </div>

      {/* Price + actions */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 }}>
        {showPrices && hasPrice && !isOutOfStock && (
          promo?.precio_final != null ? (
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 13, color: "#999", textDecoration: "line-through" }}>
                ${displayPrice.toFixed(2)}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#e53e3e", whiteSpace: "nowrap" }}>
                ${promo.precio_final.toFixed(2)}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 20, fontWeight: 700, color: primary, whiteSpace: "nowrap" }}>
              {showDesde && <span style={{ fontSize: 13, fontWeight: 400 }}>Desde </span>}
              ${displayPrice.toFixed(2)}
            </div>
          )
        )}
        <div style={{
          display: "flex", gap: 8, alignItems: "center",
          overflow: quickBuy ? "visible" : "hidden",
          maxHeight: quickBuy ? "none" : (hovered ? 44 : 0),
          opacity: quickBuy ? 1 : (hovered ? 1 : 0),
          transition: quickBuy ? "none" : "max-height 0.28s cubic-bezier(0.4,0,0.2,1), opacity 0.2s ease",
        }}>
          <button
            style={{
              padding: "8px 16px", borderRadius: rad,
              border: `1.5px solid ${primary}`,
              background: addHov ? "transparent" : primary,
              color: addHov ? primary : primaryText,
              fontWeight: 600, fontSize: 12,
              cursor: (hasPrice && !isOutOfStock) ? "pointer" : "not-allowed",
              transition: "background 0.18s, color 0.18s",
              display: "flex", alignItems: "center", gap: 5,
              opacity: (hasPrice && !isOutOfStock) ? 1 : 0.35,
            }}
            onMouseEnter={() => setAddHov(true)}
            onMouseLeave={() => setAddHov(false)}
            onClick={e => { e.stopPropagation(); if (hasPrice && !isOutOfStock) onAdd(producto); }}
            disabled={user && (!hasPrice || isOutOfStock || loadingId === producto.id)}>
            <ShoppingCartOutlined style={{ fontSize: 13 }}/>
            {loadingId === producto.id ? "Agregando..." : isOutOfStock ? "Sin stock" : producto.tiene_variantes ? "Ver opciones" : "Agregar al carrito"}
          </button>
          {!isMobile && (
            <button
              style={{
                width: 38, height: 38, flexShrink: 0, borderRadius: rad,
                border: `1.5px solid ${primary}`,
                background: eyeHov ? "transparent" : primary,
                color: eyeHov ? primary : primaryText,
                cursor: "pointer",
                transition: "background 0.18s, color 0.18s",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
              onMouseEnter={() => setEyeHov(true)}
              onMouseLeave={() => setEyeHov(false)}
              onClick={e => { e.stopPropagation(); navigate(clientPath(`/catalogo/${producto.id}`)); }}>
              <EyeOutlined style={{ fontSize: 15 }}/>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export const Catalogo = () => {

  const { addItems } = useCarrito();
  const { theme, clientId, config , clientPath} = useClient();
  const { user } = useAuth();
  const rad = resolveRad(config?.uiConfig?.t4?.brand?.radius);
  const pl = config?.uiConfig?.t4?.productList || {};
  const viewMode = pl.viewMode || "grid";
  const pageBg = pl.bgColor || undefined;
  const quickBuy = !!pl.quickBuy;
  const showStock = !!pl.showStock;
  const showOutOfStock = !!pl.showOutOfStock;
  const fontFamily = config?.uiConfig?.t4?.brand?.font || "inherit";
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [productos, setProductos] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const [categoriaIds, setCategoriaIds] = useState(() => parseCategoriaIds(searchParams.get("categoria_id")));
  const [loadingId, setLoadingId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [total, setTotal] = useState(0);
  const isMobile = useMediaQuery({ query: `(max-width: 700px)` });
  const [filterOpen, setFilterOpen] = useState(false);

  const handleSelectCategorias = (ids) => {
    setCategoriaIds(ids);
    setPage(1);
    const next = new URLSearchParams(searchParams);
    if (ids.length > 0) next.set("categoria_id", ids.join(","));
    else next.delete("categoria_id");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const nextIds = parseCategoriaIds(searchParams.get("categoria_id"));
    setCategoriaIds(prev => sameIds(prev, nextIds) ? prev : nextIds);
    const nextQ = searchParams.get("q") || "";
    setQ(prev => prev !== nextQ ? nextQ : prev);
  }, [searchParams]);

  useEffect(() => {
    let active = true;
    setIsFetching(true);
    api.catalogo.getProductos({ q, categoria_id: categoriaIds, page, limit: pageSize })
      .then(data => {
        if (active) {
          setProductos(data?.data || []);
          setTotal(data?.total ?? 0);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (active) {
          setIsFetching(false);
          setInitialLoading(false);
        }
      });
    return () => { active = false; };
  }, [q, categoriaIds, page, pageSize]);

  const handleAgregarCarrito = async (producto) => {
    if (producto.tiene_variantes) { navigate(clientPath(`/catalogo/${producto.id}`)); return; }
    // Mirar es libre; para armar el carrito hace falta sesión. Después del
    // login vuelve acá.
    if (!user) { navigate(clientPath('/login'), { state: { volverA: location.pathname + location.search } }); return; }
    setLoadingId(producto.id);
    const res = await addItems({
      id_producto: producto.id,
      precio_unit: producto.precio,
      producto: producto.nombre,
      codigo: producto.codigo,
    }, 1);
    if (res?.status === "success") message.success("Producto agregado al carrito");
    else message.error("No se pudo agregar el producto");
    setLoadingId(null);
  };

  const primary = theme.primaryColor || "#3b5bdb";
  const sharedCardProps = { theme, rad, onAdd: handleAgregarCarrito, loadingId, clientId, clientPath, navigate, quickBuy, showStock, showOutOfStock, showPrices: true, isMobile, user };

  return (
    <div style={{ padding: isMobile ? "20px 16px" : "32px 32px", minHeight: "70vh", background: pageBg, fontFamily }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <h1 style={{
          fontSize: isMobile ? 22 : 28, fontWeight: 800, margin: 0,
          paddingLeft: 14, borderLeft: `4px solid ${primary}`,
          color: theme.textColor || "#1a1a2e",
        }}>
          Productos
        </h1>
        <Input.Search
          placeholder="Buscar por código o descripción..."
          defaultValue={q}
          key={q}
          onSearch={val => { setQ(val); setPage(1); }}
          style={{ width: isMobile ? "100%" : 320 }}
          prefix={<SearchOutlined />}
          allowClear
        />
      </div>

      {/* Mobile filter drawer trigger */}
      {isMobile && (
        <>
          <div style={{ marginBottom: 16 }}>
            <Badge count={categoriaIds.length} size="small" offset={[-4, 4]}>
              <Button
                icon={<SlidersOutlined />}
                onClick={() => setFilterOpen(true)}
                style={{ borderRadius: 8, fontWeight: 600, fontSize: 13 }}
              >
                Filtros
              </Button>
            </Badge>
          </div>
          <Drawer
            open={filterOpen}
            onClose={() => setFilterOpen(false)}
            placement="left"
            width={300}
            styles={{ body: { padding: 0 }, header: { display: "none" } }}
            title={null}
            closable={false}
          >
            <FiltrosCatalogo
              categoriaIds={categoriaIds}
              onSelect={handleSelectCategorias}
              noBorder
              onClose={() => setFilterOpen(false)}
            />
          </Drawer>
        </>
      )}

      {/* Main layout */}
      <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
        {/* Desktop sidebar */}
        {!isMobile && (
          <FiltrosCatalogo categoriaIds={categoriaIds} onSelect={handleSelectCategorias} />
        )}

        {/* Products area */}
        <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
          {isFetching && !initialLoading && (
            <div style={{
              position: "absolute",
              top: -6,
              right: 0,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 10px",
              borderRadius: 999,
              background: "rgba(255,255,255,0.92)",
              border: "1px solid rgba(0,0,0,0.06)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              color: theme.textColor || "#4b5563",
              fontSize: 12,
              fontWeight: 600,
            }}>
              <Spin size="small" />
              Actualizando
            </div>
          )}
          {initialLoading ? (
            <div style={{ textAlign: "center", padding: 60 }}><Spin size="large" /></div>
          ) : isFetching && productos.length === 0 ? (
            <div style={{ textAlign: "center", padding: 60, color: theme.textColor || "#4b5563" }}>
              <Spin />
              <p style={{ marginTop: 12, fontSize: 13 }}>Actualizando productos</p>
            </div>
          ) : (showOutOfStock ? productos : productos.filter(p => !sinStock(p))).length > 0 ? (
            <div style={{
              opacity: isFetching ? 0.58 : 1,
              transition: "opacity 0.18s ease",
              pointerEvents: isFetching ? "none" : "auto",
            }}>
              {(() => {
                const lista = showOutOfStock ? productos : productos.filter(p => !sinStock(p));
                return viewMode === "list" && !isMobile ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {lista.map(producto => (
                      <ProductListItem key={producto.id} producto={producto} {...sharedCardProps}/>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "repeat(2, 1fr)"
                      : "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: isMobile ? 12 : 20,
                    alignItems: "stretch",
                  }}>
                    {lista.map(producto => (
                      <ProductCard key={producto.id} producto={producto} {...sharedCardProps}/>
                    ))}
                  </div>
                );
              })()}
            </div>
          ) : (
            <div style={{ textAlign: "center", paddingTop: 40 }}>
              <img src={img} alt="sin resultados" style={{ height: isMobile ? 180 : 240, width: "auto" }} />
              <p style={{ color: "#aaa", marginTop: 16 }}>Sin resultados</p>
            </div>
          )}

          {total > pageSize && (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 32 }}>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                showSizeChanger
                pageSizeOptions={["12", "24", "48", "96"]}
                onChange={(p, ps) => {
                  setPage(p);
                  if (ps !== pageSize) { setPageSize(ps); setPage(1); }
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
